import {
  type APIChannel,
  type APIMessage,
  ChannelType,
  compareSnowflakes,
  selectUnreadChannels,
  THREAD_CHANNEL_TYPES,
} from '@alternativamais/chat-core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { avisarErro } from '../../../componentes/Avisos';
import { Icone } from '../../../componentes/Icone';
import { useCliente, useDerivado, useEstado } from '../../../chat/contexto';
import { rotaDoCanal } from '../Canal';
import {
  BotaoIr,
  CaixaPopover,
  Carregando,
  MensagemResumida,
  Vazio,
} from '../componentes';

type Aba = 'para-voce' | 'nao-lidas' | 'mencoes';

/**
 * Caixa de entrada (C9): "Para você" (menções ainda não lidas, conversas
 * diretas e threads que você segue com novidade), "Não lidas" (canal por
 * canal) e "Menções" (`GET /users/@me/mentions`).
 */
export function CaixaDeEntrada({ aoFechar }: { aoFechar: () => void }) {
  const cliente = useCliente();
  const [aba, setAba] = useState<Aba>('para-voce');
  const naoLidas = useDerivado(
    (estado) => selectUnreadChannels(estado),
    (estado) => [
      estado.readStates,
      estado.channels,
      estado.guildSettings,
      estado.joinedThreads,
      estado.privateChannelIds,
    ],
  );
  const marcarTudo = async () => {
    const lista =
      aba === 'para-voce'
        ? naoLidas.filter((canal) => paraVoce(cliente.store.getState(), canal))
        : naoLidas;
    for (const canal of lista) {
      await cliente.ack(canal.id).catch(() => undefined);
    }
  };
  return (
    <CaixaPopover
      titulo="Caixa de entrada"
      icone="caixa-entrada"
      largura={480}
      altura={620}
      aoFechar={aoFechar}
      acoes={
        aba !== 'mencoes' && naoLidas.length > 0 ? (
          <button
            type="button"
            onClick={() => void marcarTudo()}
            className="h-7 rounded-md px-2 text-[13px] font-semibold text-link hover:underline"
          >
            Marcar tudo como lido
          </button>
        ) : undefined
      }
    >
      <div
        role="tablist"
        aria-label="Filtrar caixa de entrada"
        className="flex gap-1 border-b border-borda px-3 py-2"
      >
        {(
          [
            ['para-voce', 'Para você'],
            ['nao-lidas', 'Não lidas'],
            ['mencoes', 'Menções'],
          ] as const
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            role="tab"
            aria-selected={aba === valor}
            onClick={() => setAba(valor)}
            className={`h-7 rounded-md px-2.5 text-sm font-semibold ${
              aba === valor
                ? 'bg-ativo text-texto'
                : 'text-texto-2 hover:text-texto'
            }`}
          >
            {rotulo}
          </button>
        ))}
      </div>
      {aba === 'mencoes' ? (
        <Mencoes aoIr={aoFechar} />
      ) : (
        <ListaNaoLidas
          canais={
            aba === 'para-voce'
              ? naoLidas.filter((canal) =>
                  paraVoce(cliente.store.getState(), canal),
                )
              : naoLidas
          }
          aba={aba}
          aoIr={aoFechar}
        />
      )}
    </CaixaPopover>
  );
}

/** Conversa direta, thread seguida ou canal com menção não lida. */
function paraVoce(
  estado: ReturnType<ReturnType<typeof useCliente>['store']['getState']>,
  canal: APIChannel,
) {
  return (
    !canal.guild_id ||
    !!estado.joinedThreads[canal.id] ||
    (estado.readStates[canal.id]?.mention_count ?? 0) > 0
  );
}

function ListaNaoLidas({
  canais,
  aba,
  aoIr,
}: {
  canais: APIChannel[];
  aba: Aba;
  aoIr: () => void;
}) {
  if (!canais.length) {
    return (
      <Vazio
        icone="check"
        titulo="Tudo em dia"
        texto={
          aba === 'para-voce'
            ? 'Nenhuma menção, conversa ou thread com novidade.'
            : 'Nenhum canal com mensagem nova.'
        }
      />
    );
  }
  return (
    <div className="flex flex-col gap-3 p-3">
      {canais.slice(0, 25).map((canal) => (
        <CanalNaoLido key={canal.id} canalId={canal.id} aoIr={aoIr} />
      ))}
    </div>
  );
}

/** Um canal não lido: as mensagens novas (até 3) e "marcar como lido". */
function CanalNaoLido({
  canalId,
  aoIr,
}: {
  canalId: string;
  aoIr: () => void;
}) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const canal = useEstado((estado) => estado.channels[canalId]);
  const lido = useEstado(
    (estado) => estado.readStates[canalId]?.last_message_id ?? null,
  );
  const mencoes = useEstado(
    (estado) => estado.readStates[canalId]?.mention_count ?? 0,
  );
  const [mensagens, setMensagens] = useState<APIMessage[] | null>(null);

  useEffect(() => {
    let vivo = true;
    cliente.api
      .messages(canalId, lido ? { after: lido, limit: 3 } : { limit: 3 })
      .then((lista) => {
        if (!vivo) return;
        setMensagens(
          [...lista].sort((a, b) => compareSnowflakes(a.id, b.id)).slice(-3),
        );
      })
      .catch(() => vivo && setMensagens([]));
    return () => {
      vivo = false;
    };
  }, [cliente, canalId, lido]);

  if (!canal) return null;
  return (
    <section className="flex flex-col gap-2 rounded-lg bg-lateral p-2.5">
      <header className="flex items-center gap-2 px-1">
        <TituloDoCanal canal={canal} />
        {mencoes > 0 && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-lg bg-perigo px-[5px] text-[11px] font-bold text-white">
            {mencoes}
          </span>
        )}
        <span className="grow" />
        <button
          type="button"
          aria-label="Marcar como lido"
          title="Marcar como lido"
          onClick={() => cliente.ack(canalId).catch(avisarErro)}
          className="flex size-7 items-center justify-center rounded-md text-texto-3 hover:bg-ativo hover:text-texto"
        >
          <Icone nome="check" tamanho={16} />
        </button>
      </header>
      {mensagens === null ? (
        <Carregando />
      ) : (
        mensagens.map((mensagem) => (
          <MensagemResumida
            key={mensagem.id}
            mensagem={mensagem}
            guildId={canal.guild_id ?? null}
            data={new Date(mensagem.timestamp).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
            acoes={
              <BotaoIr
                aoClicar={() => {
                  aoIr();
                  navegar(`${rotaDoCanal(canal)}/${mensagem.id}`);
                }}
              />
            }
          />
        ))
      )}
    </section>
  );
}

function TituloDoCanal({ canal }: { canal: APIChannel }) {
  const servidor = useEstado((estado) =>
    canal.guild_id ? estado.guilds[canal.guild_id]?.name : undefined,
  );
  const pai = useEstado((estado) =>
    canal.parent_id ? estado.channels[canal.parent_id]?.name : undefined,
  );
  const dm = canal.type === ChannelType.DM;
  const grupo = canal.type === ChannelType.GROUP_DM;
  const thread = THREAD_CHANNEL_TYPES.has(canal.type);
  const nome = dm
    ? (canal.recipients?.[0]?.global_name ?? canal.recipients?.[0]?.username)
    : grupo
      ? canal.name ||
        (canal.recipients ?? [])
          .map((pessoa) => pessoa.global_name ?? pessoa.username)
          .join(', ')
      : canal.name;
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <Icone
        nome={dm ? 'arroba' : grupo ? 'pessoas' : thread ? 'thread' : 'hash'}
        tamanho={16}
        className="shrink-0 text-texto-3"
      />
      <span className="truncate text-sm font-semibold">{nome}</span>
      <span className="truncate text-xs text-texto-3">
        {dm
          ? 'Mensagem direta'
          : grupo
            ? 'Grupo'
            : [pai, servidor].filter(Boolean).join(' · ')}
      </span>
    </span>
  );
}

/** Menções recentes, de 25 em 25. */
function Mencoes({ aoIr }: { aoIr: () => void }) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const [mensagens, setMensagens] = useState<APIMessage[] | null>(null);
  const [mais, setMais] = useState(false);
  const [carregando, setCarregando] = useState(false);

  const carregar = useMemo(
    () => (antes?: string) => {
      return cliente.api
        .recentMentions({ limit: 25, ...(antes ? { before: antes } : {}) })
        .then((lista) => {
          cliente.store
            .getState()
            .upsertUsers(lista.map((item) => item.author));
          setMensagens((atuais) => [
            ...(antes ? (atuais ?? []) : []),
            ...lista,
          ]);
          setMais(lista.length === 25);
        })
        .catch((erro) => {
          avisarErro(erro);
          setMensagens((atuais) => atuais ?? []);
        })
        .finally(() => setCarregando(false));
    },
    [cliente],
  );

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (mensagens === null) return <Carregando />;
  if (!mensagens.length) {
    return (
      <Vazio
        icone="arroba"
        titulo="Nenhuma menção"
        texto="Quando alguém citar você, um cargo seu ou todos, aparece aqui."
      />
    );
  }
  return (
    <div className="flex flex-col gap-2 p-3">
      {mensagens.map((mensagem) => (
        <div key={mensagem.id} className="flex flex-col gap-1">
          <CanalDaMencao canalId={mensagem.channel_id} />
          <MensagemResumida
            mensagem={mensagem}
            guildId={mensagem.guild_id ?? null}
            acoes={
              <BotaoIr
                aoClicar={() => {
                  aoIr();
                  navegar(
                    `${rotaDoCanal({ id: mensagem.channel_id, guild_id: mensagem.guild_id })}/${mensagem.id}`,
                  );
                }}
              />
            }
          />
        </div>
      ))}
      {mais && (
        <button
          type="button"
          disabled={carregando}
          onClick={() => {
            setCarregando(true);
            void carregar(mensagens[mensagens.length - 1]?.id);
          }}
          className="h-9 rounded-lg text-sm font-semibold text-link hover:underline disabled:opacity-50"
        >
          {carregando ? 'Carregando…' : 'Ver mais'}
        </button>
      )}
    </div>
  );
}

function CanalDaMencao({ canalId }: { canalId: string }) {
  const canal = useEstado((estado) => estado.channels[canalId]);
  if (!canal) return null;
  return (
    <div className="px-1">
      <TituloDoCanal canal={canal} />
    </div>
  );
}
